import { useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, X, Star, Loader2 } from "lucide-react";

const MultiPhotoUploader = ({ photos = [], cover, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    setUploading(true);
    const newPhotos = [...photos];
    let newCover = cover;
    try {
      for (const file of files) {
        if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
          toast.error(`${file.name}: only JPG/PNG/WEBP allowed`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: max 10MB per photo`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPhotos.push(data.file_url);
        if (!newCover) newCover = data.file_url;
      }
      onChange(newPhotos, newCover);
      toast.success("Photos uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = (url) => {
    const next = photos.filter((p) => p !== url);
    let newCover = cover;
    if (cover === url) newCover = next[0] || null;
    onChange(next, newCover);
  };

  const setCover = (url) => onChange(photos, url);

  return (
    <div data-testid="multi-photo-uploader">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {photos.map((url) => (
          <div key={url} className="relative group" data-testid={`photo-${url.split('/').pop()}`}>
            <img src={fileUrl(url)} alt="" className="w-full aspect-square object-cover rounded-lg border border-[#E2E8F0]" />
            {cover === url && (
              <div className="absolute top-1.5 left-1.5 bg-[#06B6D4] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star className="h-2.5 w-2.5 fill-current" /> Cover
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              {cover !== url && (
                <button onClick={() => setCover(url)} type="button"
                  data-testid={`set-cover-${url.split('/').pop()}`}
                  className="bg-[#06B6D4] text-white text-xs px-2 py-1 rounded font-semibold flex items-center gap-1">
                  <Star className="h-3 w-3" /> Set cover
                </button>
              )}
              <button onClick={() => remove(url)} type="button"
                data-testid={`remove-photo-${url.split('/').pop()}`}
                className="bg-red-500 text-white text-xs px-2 py-1 rounded font-semibold flex items-center gap-1">
                <X className="h-3 w-3" /> Remove
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="add-photo-button"
          className="aspect-square border-2 border-dashed border-[#E2E8F0] rounded-lg flex flex-col items-center justify-center gap-1 text-neutral-500 hover:border-[#0B1E3F] hover:text-[#0B1E3F] transition-colors disabled:opacity-50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
          <span className="text-xs">{uploading ? "Uploading…" : "Add photo"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
        data-testid="multi-photo-input"
        onChange={(e) => e.target.files && upload(Array.from(e.target.files))} />
      <p className="text-xs text-neutral-500 mt-2">Click the star icon on any photo to set it as the cover image. JPG/PNG/WEBP up to 10MB each.</p>
    </div>
  );
};

export default MultiPhotoUploader;
